import React from "react";
import ReactMarkdown from "react-markdown";
import {
  Box,
  Typography,
  Button,
  Grid,
  CircularProgress,
  IconButton,
  Popover,
  Dialog,
  DialogActions,
} from "@material-ui/core";
import ImageIcon from "@material-ui/icons/Image";
import ScheduleIcon from "@material-ui/icons/Schedule";
import LayersIcon from "@material-ui/icons/Layers";
import ExtensionIcon from "@material-ui/icons/Extension";
import InfoOutlinedIcon from "@material-ui/icons/InfoOutlined";
import StorefrontIcon from "@material-ui/icons/Storefront";
import StarIcon from "@material-ui/icons/Star";

import MainAppBar from "../../components/MainAppBar";
import CustomAlert from "../../components/CustomAlert/CustomAlert";

const BoutiqueView = ({
  classes,
  t,
  templateData,
  isLoading,
  errorMessage,
  handleResetError,
  selectedThemeIndex,
  setSelectedThemeIndex,
  featuredTours,
  regularTours,
  copyingTourId,
  handleCopyTour,
  descriptionAnchorEl,
  selectedTourForDescription,
  handleOpenDescription,
  handleCloseDescription,
  requestDialogTour,
  isRequesting,
  requestSuccess,
  handleOpenRequestDialog,
  handleCloseRequestDialog,
  handleConfirmRequest,
}) => {
  return (
    <>
      <MainAppBar showHomeButton showAdminButton={false} />

      <Box className={classes.mainContent}>
        {/* ── En-tête ── */}
        <Box className={classes.pageHeader}>
          <Box style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 6 }}>
            <StorefrontIcon style={{ color: "#FF7043", fontSize: 28 }} />
            <Typography className={classes.pageTitle}>
              {t("boutique.title")}
            </Typography>
          </Box>
          <Typography className={classes.pageSubtitle}>
            {t("boutique.subtitle")}
          </Typography>
        </Box>

        <Box className={classes.pageBody}>

          {isLoading ? (
            <Box className={classes.loadingContainer}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              {/* ── Filtres thèmes ── */}
              <Box className={classes.filtersRow}>
                <Button
                  size="small"
                  className={`${classes.filterChip} ${
                    selectedThemeIndex === 0 ? classes.filterChipActive : classes.filterChipInactive
                  }`}
                  onClick={() => setSelectedThemeIndex(0)}
                >
                  {t("boutique.all_themes")}
                </Button>
                {templateData.map((theme, index) => (
                  <Button
                    key={theme.id}
                    size="small"
                    className={`${classes.filterChip} ${
                      selectedThemeIndex === index + 1
                        ? classes.filterChipActive
                        : classes.filterChipInactive
                    }`}
                    onClick={() => setSelectedThemeIndex(index + 1)}
                  >
                    {theme.name}
                  </Button>
                ))}
              </Box>

              {/* ── Activités sur demande (hero) ── */}
              {featuredTours.length > 0 && (
                <Box style={{ marginBottom: 40 }}>
                  <Typography className={classes.sectionTitle}>
                    <StarIcon style={{ color: "#FF7043", fontSize: 20 }} />
                    {t("boutique.featured_section")}
                  </Typography>

                  {featuredTours.map((tour) => {
                    const displayZones = tour.nbZones != null ? tour.nbZones : tour.zonesCount;
                    const displayEnigmas = tour.nbEnigmas != null ? tour.nbEnigmas : tour.enigmasCount;
                    return (
                      <Box key={tour.id} className={classes.heroCard}>
                        {/* Image */}
                        <Box className={classes.heroImageWrapper}>
                          {tour.imageUrl ? (
                            <img
                              src={tour.imageUrl}
                              alt={tour.title}
                              className={classes.heroImage}
                            />
                          ) : (
                            <Box className={classes.heroImagePlaceholder}>
                              <ImageIcon style={{ color: "#fff", fontSize: 60, opacity: 0.4 }} />
                            </Box>
                          )}
                          <Box className={classes.heroImageOverlay} />
                          <Box className={classes.heroBadgeWrapper}>
                            <Typography className={classes.heroBadgeOnRequest}>
                              {t("boutique.badge_on_request")}
                            </Typography>
                            {tour.themeName && (
                              <Typography className={classes.heroThemeBadge}>
                                {tour.themeName}
                              </Typography>
                            )}
                          </Box>
                        </Box>

                        {/* Contenu */}
                        <Box className={classes.heroContent}>
                          <Typography className={classes.heroTitle}>
                            {tour.title}
                          </Typography>

                          {tour.description && (
                            <Typography className={classes.heroDescription}>
                              {tour.description}
                            </Typography>
                          )}

                          {(displayZones != null || displayEnigmas != null || tour.durationMinutes) && (
                            <Box className={classes.heroStats}>
                              {tour.durationMinutes && (
                                <Box className={classes.heroStatItem}>
                                  <ScheduleIcon style={{ fontSize: 18, color: "#FF7043" }} />
                                  <span>
                                    <span className={classes.heroStatValue}>{tour.durationMinutes}</span> min
                                  </span>
                                </Box>
                              )}
                              {displayZones != null && (
                                <Box className={classes.heroStatItem}>
                                  <LayersIcon style={{ fontSize: 18, color: "#5E77FF" }} />
                                  <span>
                                    <span className={classes.heroStatValue}>{displayZones}</span>{" "}
                                    {t("boutique.zones")}
                                  </span>
                                </Box>
                              )}
                              {displayEnigmas != null && (
                                <Box className={classes.heroStatItem}>
                                  <ExtensionIcon style={{ fontSize: 18, color: "#22AA00" }} />
                                  <span>
                                    <span className={classes.heroStatValue}>{displayEnigmas}</span>{" "}
                                    {t("boutique.enigmas")}
                                  </span>
                                </Box>
                              )}
                            </Box>
                          )}

                          <Box className={classes.heroActions}>
                            {tour.moreInfoUrl && (
                              <Button
                                variant="outlined"
                                className={classes.heroLinkButton}
                                component="a"
                                href={tour.moreInfoUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                {t("boutique.more_info_button")}
                              </Button>
                            )}
                            <Button
                              variant="contained"
                              className={classes.heroRequestButton}
                              onClick={() => handleOpenRequestDialog(tour)}
                            >
                              {t("boutique.request_button")}
                            </Button>
                          </Box>
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
              )}

              {/* ── Tous les modèles ── */}
              {regularTours.length > 0 && (
                <Box>
                  <Typography className={classes.sectionTitle}>
                    {t("boutique.all_models_section")}
                  </Typography>

                  {regularTours.length === 0 ? (
                    <Typography className={classes.emptyState}>
                      {t("boutique.no_tours")}
                    </Typography>
                  ) : (
                    <Grid container spacing={2} className={classes.toursGrid}>
                      {regularTours.map((tour) => {
                        const displayZones = tour.nbZones != null ? tour.nbZones : tour.zonesCount;
                        const displayEnigmas = tour.nbEnigmas != null ? tour.nbEnigmas : tour.enigmasCount;
                        return (
                          <Grid item xs={12} sm={6} md={4} lg={3} key={`${tour.id}-${tour.themeName}`}>
                            <Box className={classes.tourCard}>
                              <Box className={classes.tourCardImageWrapper}>
                                {tour.imageUrl ? (
                                  <img
                                    src={tour.imageUrl}
                                    alt={tour.title}
                                    className={classes.tourCardImage}
                                  />
                                ) : (
                                  <Box className={classes.tourCardImagePlaceholder}>
                                    <ImageIcon style={{ color: "#fff", fontSize: 40, opacity: 0.5 }} />
                                  </Box>
                                )}
                                <IconButton
                                  className={classes.infoButton}
                                  size="small"
                                  onClick={(e) => handleOpenDescription(e, tour)}
                                >
                                  <InfoOutlinedIcon style={{ fontSize: 16 }} />
                                </IconButton>
                                {tour.durationMinutes && (
                                  <Box className={classes.durationBadge}>
                                    <ScheduleIcon style={{ fontSize: 12 }} />
                                    {tour.durationMinutes} min
                                  </Box>
                                )}
                                {tour.themeName && (
                                  <Typography className={classes.themeBadge}>
                                    {tour.themeName}
                                  </Typography>
                                )}
                              </Box>

                              <Box className={classes.tourCardContent}>
                                <Typography className={classes.tourCardTitle}>
                                  {tour.title}
                                </Typography>
                              </Box>

                              {(displayZones != null || displayEnigmas != null) && (
                                <Box className={classes.tourCardStats}>
                                  {displayZones != null && (
                                    <Box className={classes.statItem}>
                                      <LayersIcon style={{ fontSize: 14 }} />
                                      {displayZones} {t("boutique.zones")}
                                    </Box>
                                  )}
                                  {displayEnigmas != null && (
                                    <Box className={classes.statItem}>
                                      <ExtensionIcon style={{ fontSize: 14 }} />
                                      {displayEnigmas} {t("boutique.enigmas")}
                                    </Box>
                                  )}
                                </Box>
                              )}

                              <Box className={classes.tourCardActions}>
                                <Button
                                  variant="contained"
                                  className={classes.copyButton}
                                  onClick={() => handleCopyTour(tour.id)}
                                  disabled={copyingTourId !== null}
                                  size="small"
                                >
                                  {copyingTourId === tour.id ? (
                                    <CircularProgress size={16} style={{ color: "white" }} />
                                  ) : (
                                    t("boutique.copy_button")
                                  )}
                                </Button>
                              </Box>
                            </Box>
                          </Grid>
                        );
                      })}
                    </Grid>
                  )}
                </Box>
              )}

              {featuredTours.length === 0 && regularTours.length === 0 && (
                <Typography className={classes.emptyState}>
                  {t("boutique.no_tours")}
                </Typography>
              )}
            </>
          )}
        </Box>
      </Box>

      {/* ── Popover description ── */}
      <Popover
        open={Boolean(descriptionAnchorEl)}
        anchorEl={descriptionAnchorEl}
        onClose={handleCloseDescription}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        transformOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Box className={classes.descriptionPopover}>
          {selectedTourForDescription && (
            <>
              <Typography variant="subtitle2" gutterBottom style={{ fontWeight: 600 }}>
                {selectedTourForDescription.title}
              </Typography>
              {selectedTourForDescription.description ? (
                <ReactMarkdown
                  components={{
                    p: ({ children }) => (
                      <Typography variant="body2" color="textSecondary" paragraph>
                        {children}
                      </Typography>
                    ),
                    ul: ({ children }) => (
                      <ul style={{ paddingLeft: 16, margin: "4px 0" }}>{children}</ul>
                    ),
                    li: ({ children }) => (
                      <li>
                        <Typography variant="body2" color="textSecondary" component="span">
                          {children}
                        </Typography>
                      </li>
                    ),
                    strong: ({ children }) => (
                      <strong style={{ fontWeight: 700 }}>{children}</strong>
                    ),
                  }}
                >
                  {selectedTourForDescription.description.replace(/\n/g, "  \n")}
                </ReactMarkdown>
              ) : (
                <Typography variant="body2" color="textSecondary">
                  {t("boutique.no_description")}
                </Typography>
              )}
            </>
          )}
        </Box>
      </Popover>

      {/* ── Dialog de demande ── */}
      <Dialog
        open={Boolean(requestDialogTour)}
        onClose={!isRequesting ? handleCloseRequestDialog : undefined}
        maxWidth="xs"
        fullWidth
        PaperProps={{ style: { borderRadius: "12px" } }}
      >
        <Box style={{ padding: "24px 24px 8px" }}>
          <Typography variant="h6" style={{ fontWeight: 700, color: "#2A3142", marginBottom: 12 }}>
            {requestDialogTour?.title}
          </Typography>
          {requestSuccess ? (
            <Typography variant="body2" style={{ color: "#4CAF50", fontWeight: 500 }}>
              {t("boutique.request_success")}
            </Typography>
          ) : (
            <Box>
              {requestDialogTour?.moreInfoUrl && (
                <Typography variant="body2" color="textSecondary" style={{ marginBottom: 8 }}>
                  {t("boutique.request_more_info_prefix")}{" "}
                  <a
                    href={requestDialogTour.moreInfoUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: "#FF7043", fontWeight: 600 }}
                  >
                    {t("boutique.request_more_info_link")}
                  </a>
                </Typography>
              )}
              <Typography variant="body2" color="textSecondary">
                {t("boutique.request_description")}
              </Typography>
            </Box>
          )}
        </Box>
        <DialogActions style={{ padding: "16px 24px" }}>
          {requestSuccess ? (
            <Button
              onClick={handleCloseRequestDialog}
              variant="contained"
              style={{
                backgroundColor: "#4CAF50",
                color: "#fff",
                borderRadius: "20px",
                textTransform: "none",
                fontWeight: 600,
              }}
            >
              {t("boutique.request_close")}
            </Button>
          ) : (
            <>
              <Button
                onClick={handleCloseRequestDialog}
                disabled={isRequesting}
                style={{
                  borderRadius: "20px",
                  textTransform: "none",
                  fontWeight: 600,
                  color: "#555",
                }}
              >
                {t("boutique.request_cancel")}
              </Button>
              <Button
                onClick={handleConfirmRequest}
                variant="contained"
                disabled={isRequesting}
                style={{
                  borderRadius: "20px",
                  textTransform: "none",
                  fontWeight: 600,
                  backgroundColor: "#FF7043",
                  color: "#fff",
                }}
              >
                {isRequesting ? (
                  <CircularProgress size={16} style={{ color: "white" }} />
                ) : (
                  t("boutique.request_confirm")
                )}
              </Button>
            </>
          )}
        </DialogActions>
        </Dialog>

      <CustomAlert
        severity="error"
        message={errorMessage}
        duration={4000}
        onClose={handleResetError}
      />
    </>
  );
};

export default BoutiqueView;
